import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { isTimestampClipped, isUnknownParticipantName, ParticipantViewModel } from 'stageclip-common';

import { createAllEventParticipantsRequestedAction } from '../../../actions/participantActions';
import { ClippingPlatformState } from '../../../store/clippingPlatformState';
import { getAllWithIds, getItem } from '../../../store/selectors';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../../componentBase';
import { AppSelectField } from '../../form/appSelectField';
import { required } from '../../form/formValidations';

import styles from './mergeParticipantsFormFields.module.scss';

interface MergeParticipantsFormOwnProps {
  eventId: string;
  isTemplateNotFinalized?: boolean;
}

interface MergeParticipantsStateProps extends BaseStateProps {
  participants: ParticipantViewModel[];
}

interface MergeParticipantsDispatchProps extends BaseDispatchProps {
  loadParticipants: (c: MergeParticipantsFormFields) => void;
}

class MergeParticipantsFormFields extends ComponentBase<
  MergeParticipantsFormOwnProps,
  MergeParticipantsStateProps,
  MergeParticipantsDispatchProps
> {
  protected dataLoaded(props: any) {
    return props.participants;
  }

  protected loadData(props: any) {
    return props.loadParticipants(this);
  }

  protected loggedInRender() {
    return (
      <>
        {this.props.isTemplateNotFinalized && (
          <div className={styles.warning}>
            The clip template must be finalized before you can assign unknown participants, as the assigned clip is sent
            to render straight away.
          </div>
        )}
        <AppSelectField
          name="participantToCopyFromId"
          options={this.getParticipants()}
          label="Participant to copy from:"
          validate={[required]}
          disabled={this.props.isTemplateNotFinalized}
        />
        <div className={styles.formNote}>Displaying only participants that are not clipped</div>
      </>
    );
  }

  private getParticipants() {
    return this.props.participants
      .filter(
        p =>
          !isUnknownParticipantName(p.fullName) &&
          !isTimestampClipped(p.audioStartTimestamp) &&
          !isTimestampClipped(p.audioEndTimestamp) &&
          !isTimestampClipped(p.clipStartTimestamp) &&
          !isTimestampClipped(p.clipEndTimestamp),
      )
      .map(p => ({ value: p.id, label: `${p.fullName} - ${p.subtitle} - ${p.clientParticipantId}` }));
  }
}

function mapStateToProps(
  state: ClippingPlatformState,
  ownProps: MergeParticipantsFormOwnProps,
): MergeParticipantsStateProps {
  const event = getItem(state.entities.events, ownProps.eventId);

  return {
    ...baseMapStateToProps(state),
    participants: getAllWithIds(state.entities.participants, event.participants),
  };
}

function mapDispatchToProps(
  dispatch: Dispatch,
  ownProps: MergeParticipantsFormOwnProps,
): MergeParticipantsDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    loadParticipants: (c: MergeParticipantsFormFields) =>
      dispatch(createAllEventParticipantsRequestedAction(ownProps.eventId, c, dispatch)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MergeParticipantsFormFields);
